import React, { useState } from "react";
import { Camera, AlertCircle, RefreshCw, X } from "lucide-react";

interface CameraPermissionGuardProps {
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  errorType?: "denied" | "not_found" | "unknown";
}

export function CameraPermissionGuard({
  isOpen,
  onClose,
  onRetry,
  errorType = "denied",
}: CameraPermissionGuardProps) {
  const [activeTab, setActiveTab] = useState<"chrome" | "firefox" | "safari">("chrome");

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "#16181c",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: "12px",
          padding: "24px",
          color: "#f3f4f6",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)",
          position: "relative",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "transparent",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            padding: "4px",
          }}
        >
          <X size={20} />
        </button>

        {/* Header Icon */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "rgba(239, 68, 68, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ef4444",
            marginBottom: "16px",
          }}
        >
          {errorType === "not_found" ? <Camera size={24} /> : <AlertCircle size={24} />}
        </div>

        <h3 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 8px" }}>
          {errorType === "not_found" ? "No Camera Detected" : "Camera Access Required"}
        </h3>
        <p style={{ fontSize: "13px", color: "#9ca3af", margin: "0 0 20px", lineHeight: 1.5 }}>
          {errorType === "not_found"
            ? "StreamPersona couldn't find a connected camera. Please plug in a webcam and click retry."
            : "StreamPersona requires camera access to capture your live expression for face swapping. Please allow permissions in your browser."}
        </p>

        {errorType !== "not_found" && (
          <>
            {/* Browser Selector Tabs */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                paddingBottom: "8px",
                marginBottom: "16px",
              }}
            >
              {(["chrome", "safari", "firefox"] as const).map((b) => (
                <button
                  key={b}
                  onClick={() => setActiveTab(b)}
                  style={{
                    background: activeTab === b ? "rgba(43, 181, 168, 0.15)" : "transparent",
                    color: activeTab === b ? "#2bb5a8" : "#9ca3af",
                    border: activeTab === b ? "1px solid rgba(43, 181, 168, 0.3)" : "1px solid transparent",
                    borderRadius: "6px",
                    padding: "4px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {b}
                </button>
              ))}
            </div>

            {/* Instruction Box */}
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "8px",
                padding: "14px",
                fontSize: "12px",
                color: "#d1d5db",
                marginBottom: "20px",
                lineHeight: 1.6,
              }}
            >
              {activeTab === "chrome" && (
                <ol style={{ margin: 0, paddingLeft: "18px" }}>
                  <li>Click the <strong>Lock</strong> / <strong>Tune</strong> icon on the left of your URL bar.</li>
                  <li>Toggle <strong>Camera</strong> to <strong>Allow</strong>.</li>
                  <li>Click <strong>Retry Camera</strong> below.</li>
                </ol>
              )}
              {activeTab === "safari" && (
                <ol style={{ margin: 0, paddingLeft: "18px" }}>
                  <li>Open <strong>Safari Settings</strong> (⌘,) → <strong>Websites</strong> tab.</li>
                  <li>Click <strong>Camera</strong> on the left panel.</li>
                  <li>Find this website (<strong>{typeof window !== "undefined" ? window.location.hostname : "web.streampersona.app"}</strong>) and change it to <strong>Allow</strong>.</li>
                </ol>
              )}
              {activeTab === "firefox" && (
                <ol style={{ margin: 0, paddingLeft: "18px" }}>
                  <li>Click the <strong>Camera / Blocked</strong> icon next to the URL address.</li>
                  <li>Click the <strong>X</strong> next to "Blocked Temporarily".</li>
                  <li>Reload page or click <strong>Retry Camera</strong> below.</li>
                </ol>
              )}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: "#9ca3af",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onRetry}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              background: "#2bb5a8",
              border: "none",
              color: "#000000",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <RefreshCw size={14} />
            Retry Camera
          </button>
        </div>
      </div>
    </div>
  );
}
